import { createPosition } from "@shared/common/utils/create";
import {
  getLargestTimeUnit,
  getTimeUnitsFromContent,
} from "@shared/common/utils/timer";
import dayjs from "dayjs";
import shortUUID from "short-uuid";
import type { CreateNodeOptions, ITimerNode, PartialNode } from "../type";

const createTimerNode = (
  data?: PartialNode<ITimerNode>,
  options?: CreateNodeOptions
): ITimerNode => {
  return {
    id: shortUUID.generate(),
    pluginData: {
      contentEditorPropSeq: 0,
      contentMeta: [
        {
          type: "timer_end_date",
          name: "카운트다운 종료 일시",
        },
        {
          type: "text_content",
          name: "텍스트 내용",
        },
        {
          type: "timer_format",
          name: "카운트다운 형식",
        },
      ],
    },
    reactions: [],
    position: createPosition(data?.position),

    // 노드트리 등에서 보이는 이름
    name: "카운트다운",

    width: { value: 100, unit: "%" },
    alignHorizontal: "LEFT",

    fills: [],
    backgrounds: [],

    locked: false,

    paragraphIndent: {
      value: 0,
      unit: "em",
    },

    lineHeight: {
      value: 1.3,
      unit: "",
    },

    paragraphSpacing: {
      value: 0,
      unit: "em",
    },

    strokes: [],
    strokeWeight: 1,
    strokeAlign: "CENTER",
    strokeCap: "SQUARE",
    strokeJoin: "ROUND",
    dashPattern: [],

    textAlignHorizontal: "CENTER",
    textAlignVertical: "CENTER",
    // TODO: 현재 기본값이 HEIGHT일 뿐, 세로 자동 조정은 height 속성 여부에 의해 결정됨. WIDTH_AND_HEIGHT일 때에는 가로 자동 조정됨.
    textAutoResize: "HEIGHT",

    fontSize: {
      value: 16,
      unit: "px",
    },

    visible: true,

    effects: [],
    boxShadowEffects: [],
    fontName: { style: "normal", family: "InterRegular-ascii" },

    padding: {
      top: {
        value: 10,
        unit: "px",
      },
      bottom: {
        value: 10,
        unit: "px",
      },
      left: {
        value: 0,
        unit: "px",
      },
      right: {
        value: 0,
        unit: "px",
      },
    },
    content: "00일 00:00:00",

    // 기본값은 고정형 + 반복 없음
    timer: {
      type: "COUNTDOWN",
      repeat: null,
      startAt: null,
      endAt: dayjs().add(1, "day").toISOString(),
      endAction: { type: "NONE" },
      format: "dd일 hh:mm:ss",
      utcOffset: new Date().getTimezoneOffset(),
    },

    // 인자값
    ...data,
    ...(options?.asTemplate &&
      data?.timer && {
        timer:
          data.timer.type === "COUNTDOWN"
            ? {
                ...data.timer,
                endAt: (() => {
                  const largestTimeUnit = getLargestTimeUnit(
                    getTimeUnitsFromContent(data?.content ?? "")
                  );
                  return largestTimeUnit === "s"
                    ? dayjs().add(59, "s").toISOString()
                    : largestTimeUnit === "m"
                    ? dayjs().add(59, "m").toISOString()
                    : largestTimeUnit === "h"
                    ? dayjs().add(23, "h").toISOString()
                    : dayjs().add(1, largestTimeUnit).toISOString();
                })(),
              }
            : data.timer.type === "COUNTDOWN_REPEAT"
            ? {
                ...data.timer,
                endAt: (() => {
                  const largestTimeUnit = getLargestTimeUnit(
                    getTimeUnitsFromContent(data?.content ?? "")
                  );
                  return {
                    d: 0,
                    h: largestTimeUnit === "h" ? 23 : 0,
                    m: largestTimeUnit === "m" ? 59 : 0,
                    s: largestTimeUnit === "s" ? 59 : 0,
                    type: "AT_TIME" as const,
                  };
                })(),
              }
            : {
                ...data.timer,
              },
      }),

    // 필수값
    type: "TIMER",
  };
};

export default createTimerNode;
